import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CreditDebug = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [debugData, setDebugData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/companies');
      const companiesWithCredit = response.data.companies.filter(c => c.used_credit > 0 || c.reserved_credit > 0);
      setCompanies(companiesWithCredit);
      
      if (companiesWithCredit.length > 0) {
        setSelectedCompany(companiesWithCredit[0]);
        debugCompany(companiesWithCredit[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const debugCompany = async (companyId) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/credit/company/${companyId}/debug`);
      setDebugData(response.data);
    } catch (error) {
      console.error('Error debugging company:', error);
      alert('Error loading debug data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetCredit = async (companyId) => {
    if (!window.confirm('Are you sure you want to reset the credit for this company? This will recalculate credit usage based on actual orders.')) {
      return;
    }

    setResetting(true);
    try {
      const response = await axios.post(`/api/credit/company/${companyId}/reset`);
      alert('Credit reset successfully!');
      
      // Refresh data
      await fetchCompanies();
      await debugCompany(companyId);
      
    } catch (error) {
      console.error('Error resetting credit:', error);
      alert('Error resetting credit: ' + error.message);
    } finally {
      setResetting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('he-IL');
  };

  if (companies.length === 0) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Credit Debug</h1>
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          ✅ No companies found with credit usage. All credit balances are zero.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">🔍 Credit Debug Tool</h1>
      
      {/* Company Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Company with Credit Usage:
        </label>
        <select
          className="border border-gray-300 rounded-md px-3 py-2 w-full max-w-md"
          value={selectedCompany?.id || ''}
          onChange={(e) => {
            const company = companies.find(c => c.id === e.target.value);
            setSelectedCompany(company);
            debugCompany(e.target.value);
          }}
        >
          {companies.map(company => (
            <option key={company.id} value={company.id}>
              {company.name} - {formatCurrency(company.used_credit)} used
            </option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading debug data...</span>
        </div>
      )}

      {debugData && selectedCompany && (
        <div className="space-y-6">
          {/* Credit Status */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">💳 Credit Status - {selectedCompany.name}</h2>
              <button
                onClick={() => resetCredit(selectedCompany.id)}
                disabled={resetting}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 disabled:opacity-50"
              >
                {resetting ? 'Resetting...' : '🔄 Reset Credit'}
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded">
                <div className="text-sm text-gray-600">Credit Limit</div>
                <div className="text-lg font-semibold">{formatCurrency(debugData.credit?.credit_limit)}</div>
              </div>
              <div className="bg-red-50 p-4 rounded">
                <div className="text-sm text-gray-600">Used Credit</div>
                <div className="text-lg font-semibold text-red-600">{formatCurrency(debugData.credit?.used_credit)}</div>
              </div>
              <div className="bg-yellow-50 p-4 rounded">
                <div className="text-sm text-gray-600">Reserved Credit</div>
                <div className="text-lg font-semibold text-yellow-600">{formatCurrency(debugData.credit?.reserved_credit)}</div>
              </div>
              <div className="bg-green-50 p-4 rounded">
                <div className="text-sm text-gray-600">Available Credit</div>
                <div className="text-lg font-semibold text-green-600">{formatCurrency(debugData.credit?.available_credit)}</div>
              </div>
            </div>
          </div>

          {/* Analysis */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">📊 Analysis</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Total Orders:</span>
                <span className="ml-2 font-semibold">{debugData.analysis.total_orders}</span>
              </div>
              <div>
                <span className="text-gray-600">Pending Orders:</span>
                <span className="ml-2 font-semibold">{debugData.analysis.pending_orders}</span>
              </div>
              <div>
                <span className="text-gray-600">Delivered Unpaid:</span>
                <span className="ml-2 font-semibold">{debugData.analysis.delivered_unpaid_orders}</span>
              </div>
              <div>
                <span className="text-gray-600">Expected Used:</span>
                <span className="ml-2 font-semibold">{formatCurrency(debugData.analysis.expected_delivered_credit)}</span>
              </div>
              <div>
                <span className="text-gray-600">Actual Used:</span>
                <span className="ml-2 font-semibold">{formatCurrency(debugData.analysis.actual_used_credit)}</span>
              </div>
              <div>
                <span className="text-gray-600">Discrepancy:</span>
                <span className={`ml-2 font-semibold ${Math.abs(debugData.analysis.discrepancy) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(debugData.analysis.discrepancy)}
                </span>
              </div>
            </div>
            
            {Math.abs(debugData.analysis.discrepancy) > 0.01 && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
                <div className="text-red-800 font-semibold">⚠️ Discrepancy Found!</div>
                <div className="text-red-700 text-sm mt-1">
                  The used credit doesn't match the expected value based on order statuses. 
                  This suggests there are credit transactions that aren't properly synced with orders.
                </div>
              </div>
            )}
          </div>

          {/* Orders */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">📦 Orders with Company Credit ({debugData.orders.length})</h3>
            {debugData.orders.length === 0 ? (
              <div className="text-gray-500 italic">No orders found with company_credit payment method</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {debugData.orders.map(order => (
                      <tr key={order.id}>
                        <td className="px-4 py-2 text-sm font-medium">{order.order_number}</td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            order.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                            order.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {order.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm font-semibold">{formatCurrency(order.total_amount)}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{formatDate(order.created_at)}</td>
                        <td className="px-4 py-2 text-sm">
                          {order.user ? `${order.user.first_name} ${order.user.last_name}` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Transactions */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">💰 Recent Credit Transactions ({debugData.transactions.length})</h3>
            {debugData.transactions.length === 0 ? (
              <div className="text-gray-500 italic">No credit transactions found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {debugData.transactions.slice(0, 10).map(tx => (
                      <tr key={tx.id}>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            tx.transaction_type === 'purchase' ? 'bg-red-100 text-red-800' :
                            tx.transaction_type === 'payment' ? 'bg-green-100 text-green-800' :
                            tx.transaction_type === 'reservation' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {tx.transaction_type}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm font-semibold">
                          <span className={tx.amount < 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(tx.amount)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            tx.status === 'completed' ? 'bg-green-100 text-green-800' :
                            tx.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {tx.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">{tx.description}</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{formatDate(tx.created_at)}</td>
                        <td className="px-4 py-2 text-sm">
                          {tx.order ? tx.order.order_number : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditDebug;
